import {
  ApolloClient,
  ApolloProvider,
  from,
  HttpLink,
  InMemoryCache,
} from '@apollo/client'
import { onError } from '@apollo/client/link/error'
import ReactDOM from 'react-dom/client'
import App from './App'
import './index.css'
import reportWebVitals from './reportWebVitals'
import { ErrorCode } from './utils/ErrorCode'
import { AUTH_TOKEN } from './hooks'
import env from './config/env'

// Global state for maintenance mode
let isMaintenanceMode = false

export const setMaintenanceMode = (value: boolean) => {
  isMaintenanceMode = value
  // Trigger a custom event to notify the app
  window.dispatchEvent(
    new CustomEvent('maintenanceModeChange', { detail: value }),
  )
}

export const getMaintenanceMode = () => isMaintenanceMode

const errorLink = onError(
  ({ graphQLErrors, networkError, forward, operation }) => {
    if (graphQLErrors) {
      graphQLErrors.forEach((gqlError: any) => {
        // Check for maintenance mode
        if (
          gqlError.code === ErrorCode.maintenanceMode ||
          gqlError.extensions?.code === ErrorCode.maintenanceMode ||
          gqlError.extensions?.code === 'MaintenanceError'
        ) {
          console.log('Maintenance mode error')
          setMaintenanceMode(true)
          return
        }

        // Remove session and reload if unauthenticated call
        if (
          gqlError.code === ErrorCode.authorizationError ||
          gqlError.message === 'Forbidden resource'
        ) {
          localStorage.removeItem(AUTH_TOKEN)
          window.location.reload()
        }
      })
    }

    if (networkError) {
      console.log(`[Network error]: ${networkError}`)

      // Check if network error is maintenance mode (503 status)
      const networkErrorAny = networkError as any
      if (
        networkErrorAny?.statusCode === 503 ||
        networkErrorAny?.result?.error === 'MaintenanceError' ||
        networkErrorAny?.message?.includes('maintenance')
      ) {
        setMaintenanceMode(true)
        return
      }
    }

    forward(operation)
  },
)

const sessionToken = localStorage.getItem(AUTH_TOKEN)
const GQL_ENDPOINT = `${env.API_URL}/graphql`

const link = from([
  errorLink,
  new HttpLink({
    uri: GQL_ENDPOINT,
    headers: {
      // old
      // 'x-api-token': sessionToken ?? '',

      // new
      // Only include the x-api-token header if a valid session token exists
      // This ensures that public endpoints that don't require authentication
      // don't receive an empty x-api-token header, which could cause issues
      // on the server side
      ...(sessionToken ? { 'x-api-token': sessionToken } : {}),
      client: 'Xy72JqKBWQOZcab8EfPtSenF7kzrt5Ww',
    },
  }),
])

const mergeObjects = (existing, incoming) => {
  const newItems = [...existing.items]

  for (const item of incoming.items) {
    const index = newItems.findIndex(
      (existing) => existing.__ref === item.__ref,
    )

    if (index >= 0) {
      // need to update
      newItems[index] = item
      continue
    }

    newItems.push(item)
  }

  return {
    items: newItems,
    pagination: incoming.pagination,
  }
}

const mergeLogo = (existing = { name: '', url: '' }, incoming) => {
  if (!incoming) {
    return existing
  }

  return incoming
}

const client = new ApolloClient({
  // uri: 'http://localhost:3005/graphql',
  cache: new InMemoryCache({
    typePolicies: {
      Brand: {
        fields: {
          logo: {
            merge(existing, incoming) {
              return mergeLogo(existing, incoming)
            },
          },
        },
      },
      Query: {
        fields: {
          getMessagesThreads: {
            keyArgs: ['options', ['query', 'filters', 'sort']],
            merge(
              existing = { items: [], pagination: {} },
              incoming,
              { args },
            ) {
              if (!incoming) {
                return existing
              }

              return mergeObjects(existing, incoming)
            },
          },
          getClipsByBrandId: {
            keyArgs: ['brandId', 'options', ['query', 'filters', 'sort']],
            merge(
              existing = { items: [], pagination: {} },
              incoming,
              { args },
            ) {
              if (!incoming) {
                return existing
              }

              return mergeObjects(existing, incoming)
            },
          },
          getClipsByCampaignId: {
            keyArgs: ['campaignId', 'options', ['query', 'filters', 'sort']],
            merge(
              existing = { items: [], pagination: {} },
              incoming,
              { args },
            ) {
              if (!incoming) {
                return existing
              }

              return mergeObjects(existing, incoming)
            },
          },
          getClipsByCampaignUuid: {
            keyArgs: [
              'uuid',
              'options',
              ['query', 'filters', 'sort', 'skip', 'limit'],
            ],
            merge(
              existing = { items: [], pagination: {} },
              incoming,
              { args },
            ) {
              if (!incoming) {
                return existing
              }

              return mergeObjects(existing, incoming)
            },
          },
          getClipsByCreatorId: {
            keyArgs: ['creatorId', 'options', ['query', 'filters', 'sort']],
            merge(
              existing = { items: [], pagination: {} },
              incoming,
              { args },
            ) {
              if (!incoming) {
                return existing
              }

              return mergeObjects(existing, incoming)
            },
          },
          getClipsByBrandAndCreator: {
            keyArgs: [
              'brandId',
              'creatorId',
              'options',
              ['query', 'filters', 'sort'],
            ],
            merge(
              existing = { items: [], pagination: {} },
              incoming,
              { args },
            ) {
              if (!incoming) {
                return existing
              }

              return mergeObjects(existing, incoming)
            },
          },
          getInspirationsByBrandId: {
            keyArgs: ['brandId', 'options', ['query', 'filters', 'sort']],
            merge(
              existing = { items: [], pagination: {} },
              incoming,
              { args },
            ) {
              if (!incoming) {
                return existing
              }

              return mergeObjects(existing, incoming)
            },
          },
          getInspirations: {
            keyArgs: ['options', ['query', 'filters', 'sort']],
            merge(
              existing = { items: [], pagination: {} },
              incoming,
              { args },
            ) {
              if (!incoming) {
                return existing
              }

              return mergeObjects(existing, incoming)
            },
          },
          getInspirationsByCampaignId: {
            keyArgs: ['campaignId', 'options', ['query', 'filters', 'sort']],
            merge(
              existing = { items: [], pagination: {} },
              incoming,
              { args },
            ) {
              if (!incoming) {
                return existing
              }

              return mergeObjects(existing, incoming)
            },
          },
          getPortfolioByCreator: {
            keyArgs: ['creatorId', 'options', ['query', 'filters', 'sort']],
            merge(
              existing = { items: [], pagination: {} },
              incoming,
              { args },
            ) {
              if (!incoming) {
                return existing
              }

              return mergeObjects(existing, incoming)
            },
          },
          getCollaborationsByCampaignId: {
            keyArgs: ['campaignId', 'options', ['filters', 'sort']],
            merge(
              existing = { items: [], pagination: {} },
              incoming,
              { args },
            ) {
              if (!incoming) {
                return existing
              }

              return mergeObjects(existing, incoming)
            },
          },
        },
      },
    },
  }),
  connectToDevTools: true,
  link,
})

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement)

root.render(
  <ApolloProvider client={client}>
    <App />
  </ApolloProvider>,
)

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals()
